import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, Divider, Flex, Form, Modal, Popconfirm, Space, Tooltip, Typography, UploadFile } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { supervisorConfirmOvertime, uploadOvertimeTicketFile } from 'src/apis/hr_ticket/overtimeTicket.api';

import { useState } from 'react';
import { BtnCustom } from 'src/components/v2/Button';
import { ConfirmTypeEn } from 'src/constants/confirmTypeEn.enum';
import { LeaveTypeIdEn } from 'src/constants/leaveTypeId.enum';
import {
  ILeaveTicket,
  ILeaveTicketDetail,
  LeaveTicketAdd,
  LeaveTicketDetailAdd
} from 'src/types/hr_ticket/leaveTicket.type';
import {
  IOvertimeAttachFile,
  OverTimeTicketConfirm,
  OvertimeDetailConfirmed,
  OvertimeTicketProgress,
  OvertimeTicketProgressConfirm
} from 'src/types/hr_ticket/overtimeTicket.type';
import { IEmployeeShort } from 'src/types/master_data/employee.type';
import { showNotification } from 'src/utils/notification';
import AddConfirmJobResult from './AddConfirmJobResult';
import AddConfirmOvertimeTicket from './AddConfirmOvertimeTicket';
import AddLeaveTicketDetail from './AddLeaveTicketDetail';
import './ConfirmOvertimeTicket.scss';
import { OpenType } from './OvertimeTicketDetail';
const { Text, Title } = Typography;
interface Props {
  open: OpenType;
  setOpen: React.Dispatch<React.SetStateAction<OpenType>>;
  jobConfirmsList: OvertimeTicketProgress[];
  date: string;
  ticketCode: string;
  isSupervisorConfirmed: number;
  confirmsList: OvertimeDetailConfirmed[];
  handleDepartment: number;
  overtimeType: string;
}

type FormStateType = {
  supervisorNote: string;
  fileList: UploadFile<File>[];
  confirms: OvertimeDetailConfirmed[];
  overtimeTicketProgress: OvertimeTicketProgress[];
};

const initFormState: FormStateType = {
  confirms: [],
  overtimeTicketProgress: [],
  fileList: [],
  supervisorNote: ''
};

export default function ConfirmOvertimeTicket({
  open,
  setOpen,
  overtimeType,
  confirmsList,
  date,
  isSupervisorConfirmed,
  ticketCode,
  jobConfirmsList,
  handleDepartment
}: Props) {
  const { t } = useTranslation();
  const [formConfirm] = Form.useForm();
  const [formState, setFormState] = useState({
    ...initFormState,
    confirms: confirmsList,
    overtimeTicketProgress: jobConfirmsList
  });
  const [compensatoryLeaveDetails, setCompensatoryLeaveDetails] = useState<LeaveTicketDetailAdd[]>([]);
  const [openCompensatoryLeave, setOpenCompensatoryLeave] = useState<boolean>(false);
  const [noteCompensatoryLeave, setNoteCompensatoryLeave] = useState<string>('');
  const queryClient = useQueryClient();

  const autoNoteLeave = (note: string) => {
    return !!note ? note : `${ticketCode} ${date}`;
  };

  // const handleGetFileList = async (fileList: UploadFile<File>[]) => {
  //   const fileListFromServer: AxiosResponse<IOvertimeAttachFile[], any> = await getFileList(fileList);
  //   return fileListFromServer.data;
  // };
  const handleGetAllFile = async (overtimeTicketProgress: OvertimeTicketProgress[]) => {
    return await Promise.all(
      overtimeTicketProgress.map(async (item) => {
        const fileListFromServer: AxiosResponse<IOvertimeAttachFile[], any> = await getFileList(
          item.overtimeTicketProgressAttachAdd as UploadFile<File>[]
        );

        return fileListFromServer;
      })
    );
  };

  const handleConfirm = async (
    note: string,
    confirms: OvertimeDetailConfirmed[],
    fileList: UploadFile<File>[],
    compensatoryLeaveDetails: LeaveTicketDetailAdd[],
    noteCompensatoryLeave: string,
    overtimeTicketProgress: OvertimeTicketProgress[],
    type: ConfirmTypeEn
  ) => {
    // danh sách xác nhận
    const newOvertimeTicketConfirms = confirms
      ?.filter((item) => !item.isSupervisorUnconfirmed)
      ?.map((item, array) => {
        const confirm = {
          overtimeTicketDetailId: item.id,
          employeeId: item.employeeId,
          actualDate: item.date,
          projects: item.projects ?? confirms[0]?.projects,
          supervisorConfirmedNote: item.supervisorConfirmedNote || '',
          actualStartTime: item.actualStartTime ?? item.startTime,
          actualEndTime: item.actualEndTime ?? item.endTime,
          isOrderMeal: item.isNew ? false : item.isOrderMeal,
          isWorkAtConstructionSite: item.isWorkAtConstructionSite || false,
          isNew: item.isNew
        };

        if (item.isNew) {
          delete confirm.overtimeTicketDetailId;
        }

        return confirm;
      });

    // console.log(newOvertimeTicketConfirms);
    // danh sách nghỉ bù
    let compensatoryLeaveTickets: LeaveTicketAdd[] = [];
    if (compensatoryLeaveDetails.length > 0) {
      compensatoryLeaveTickets = compensatoryLeaveDetails.map((item) => {
        return {
          note: autoNoteLeave(noteCompensatoryLeave),
          leaveTicketDetails: [
            {
              ...item,
              leaveTypeId: Number(LeaveTypeIdEn.COMPENSATORY_LEAVE),
              leaveTicketDetailAttaches: Number(open.id) ? [{ overtimeTicketId: Number(open.id) }] : [],
              id: undefined,
              leaveStatus: undefined,
              employee: undefined,
              date: item?.date
            }
          ] as Partial<ILeaveTicketDetail>[]
        } as LeaveTicketAdd;
      });
    }

    // danh sách hình ảnh kết quả công việc
    const newOvertimeTicketProgress = [...overtimeTicketProgress] as OvertimeTicketProgressConfirm[];

    await handleGetAllFile(
      overtimeTicketProgress.filter(
        (x) => x?.overtimeTicketProgressAttachAdd && x?.overtimeTicketProgressAttachAdd?.length > 0
      )
    ).then((response) => {
      response.forEach((item, index) => {
        newOvertimeTicketProgress[index].overtimeTicketProgressAttach = item.data.map((x) => ({
          filePath: x.filePath
        }));
      });
    });

    supervisorConfirmOvertimeMutation.mutate(
      {
        supervisorNote: note,
        isSupervisorConfirmed: type,
        filePaths: [],
        confirms: newOvertimeTicketConfirms,
        unConfirmedDetails: confirms
          .filter((item) => {
            if (item.isSupervisorUnconfirmed) {
              return item.id;
            } else {
              return null;
            }
          })
          ?.map((item) => Number(item.id)),
        compensatoryLeaveTickets: compensatoryLeaveTickets as ILeaveTicket[],
        overtimeTicketProgresses: newOvertimeTicketProgress
      },
      {
        onError: () => {}
      }
    );
  };

  // Handle confirm
  const supervisorConfirmOvertimeMutation = useMutation({
    mutationFn: (overtimeTicketConfirm: OverTimeTicketConfirm) => {
      return supervisorConfirmOvertime(open.id as number, overtimeTicketConfirm);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['overtimeTicketsV2']
      });
      queryClient.invalidateQueries({
        queryKey: ['overtimeTicket', String(open.id)]
      });
      showNotification('Xác nhận phiếu thành công', 'success');
      setCompensatoryLeaveDetails([]);
      setOpen({
        isOpen: false
      });
      formConfirm.resetFields();
      setFormState(initFormState);
    }
  });

  // const updateOvertimeDetailsMutation = useMutation({
  //   mutationFn: (overtimeDetails: OvertimeTicketDetailAdd[]) =>
  //     updateOvertimeTicketDetail(open.id as number, overtimeDetails)
  // });

  // Handle upload file
  const uploadTicketFileMutation = useMutation({
    mutationFn: (body: FormData) => uploadOvertimeTicketFile(body as FormData)
  });

  const getFileList = async (fileLists: UploadFile<File>[]) => {
    const formData = new FormData();
    fileLists.length > 0 &&
      fileLists.forEach(function (file) {
        formData.append('files', file.originFileObj as Blob);
      });
    return uploadTicketFileMutation.mutateAsync(formData, {
      onError: () => {
        alert('Lỗi ảnh');
      }
    });
  };

  const handleSubmit = (type: ConfirmTypeEn) => {
    formConfirm
      .validateFields()
      .then(() => {
        const notjetConfirmItem = formState?.confirms?.find(
          (item) => (!item.actualStartTime || !item.actualEndTime) && !item.isSupervisorUnconfirmed
        );
        if (!!notjetConfirmItem) {
          showNotification(
            `Nhân viên ${notjetConfirmItem.employee?.fullName} chưa được xác nhận thời gian tăng ca, vui lòng chọn SỬA và điền các trường bắt buộc`,
            'warning'
          );
          return;
        }

        const notjetConfirmProgressItem = formState.overtimeTicketProgress?.find((x) => !x.note);
        if (!!notjetConfirmProgressItem) {
          showNotification(
            `Công việc ${notjetConfirmProgressItem.manpowerSupplyFormDetail?.jobTitle} chưa được xác nhận, vui lòng chọn SỬA và điền các trường bắt buộc!`,
            'warning'
          );
          return;
        }
        // const newAddDetails = formState.confirms?.filter((item) => item.isNew);
        // const oldDetails = formState.confirms?.filter((item) => !item.isNew);

        if (openCompensatoryLeave) {
          if (compensatoryLeaveDetails.length <= 0) {
            showNotification('Vui lòng thêm chi tiết phiếu nghỉ bù!', 'warning');
            return;
          }

          handleConfirm(
            formState.supervisorNote,
            formState.confirms,
            formState.fileList,
            compensatoryLeaveDetails,
            noteCompensatoryLeave,
            formState.overtimeTicketProgress,
            type
          );
        } else {
          handleConfirm(
            formState.supervisorNote,
            formState.confirms,
            formState.fileList,
            [],
            '',
            formState.overtimeTicketProgress,
            type
          );
        }

        // updateOvertimeDetailsMutation.mutate(newAddDetails, {
        //   onSuccess: () => {
        //     if (openCompensatoryLeave) {
        //       if (compensatoryLeaveDetails.length <= 0) {
        //         showNotification('Vui lòng thêm chi tiết phiếu nghỉ bù!', 'warning');
        //         return;
        //       }

        //       handleConfirm(
        //         formState.supervisorNote,
        //         formState.confirms,
        //         formState.fileList,
        //         compensatoryLeaveDetails,
        //         noteCompensatoryLeave,
        //         formState.overtimeTicketProgress,
        //         type
        //       );
        //     } else {
        //       handleConfirm(
        //         formState.supervisorNote,
        //         formState.confirms,
        //         formState.fileList,
        //         [],
        //         '',
        //         formState.overtimeTicketProgress,
        //         type
        //       );
        //     }
        //   }
        // });
      })
      .catch(() => {
        if (compensatoryLeaveDetails.length <= 0 && openCompensatoryLeave) {
          showNotification('Vui lòng thêm chi tiết phiếu nghỉ bù!', 'warning');
          return;
        }
      });
  };

  const handleCloseModal = () => {
    setOpen({ ...open, isOpen: false });
    formConfirm.resetFields();
    setFormState(initFormState);
  };

  const handleSetCompensatoryLeaveDetails = (leaveTicketDetails: LeaveTicketDetailAdd[]) => {
    setCompensatoryLeaveDetails(leaveTicketDetails);
  };

  const handleSetFormState = (overtimeTicketDetails: OvertimeDetailConfirmed[]) => {
    setFormState({ ...formState, confirms: overtimeTicketDetails });
  };

  const handleSetProgressFormState = (overtimeTicketProgress: OvertimeTicketProgress[]) => {
    setFormState({ ...formState, overtimeTicketProgress: overtimeTicketProgress });
  };

  return (
    <Modal
      title={<Text>Kết quả công việc</Text>}
      className='overtime-confirm-modal'
      centered
      maskClosable={false}
      open={open.isOpen}
      onOk={() => handleSubmit(ConfirmTypeEn.Confirmed)}
      onCancel={handleCloseModal}
      footer={[
        <Button key='back' onClick={handleCloseModal}>
          {t('action.close')}
        </Button>,
        <Popconfirm title='Bạn chắc chắn muốn từ chối xác nhận' onConfirm={() => handleSubmit(ConfirmTypeEn.Reject)}>
          <BtnCustom
            type='noConfirm'
            size='middle'
            isLoading={supervisorConfirmOvertimeMutation.isLoading || uploadTicketFileMutation.isLoading}
          />
        </Popconfirm>,
        <Button
          key='submit'
          type='primary'
          disabled={formState.confirms.every((item) => item.isSupervisorUnconfirmed)}
          onClick={() => handleSubmit(ConfirmTypeEn.Confirmed)}
          loading={supervisorConfirmOvertimeMutation.isLoading || uploadTicketFileMutation.isLoading}
        >
          {openCompensatoryLeave ? t('Xác nhận và tạo phiếu nghỉ bù') : t('Xác nhận')}
        </Button>
      ]}
    >
      <Flex vertical className='overtime-confirm-wrapper'>
        <Flex
          vertical
          gap={5}
          style={{
            marginTop: '5px',
            marginBottom: '16px'
          }}
        >
          <Space size={[4, 0]}>
            <Text type='danger'>*</Text>
            <Text strong>Xác nhận kết quả công việc</Text>
          </Space>
          <AddConfirmJobResult
            jobConfirms={formState.overtimeTicketProgress}
            handleSetFormState={handleSetProgressFormState}
            isSupervisorConfirmed={isSupervisorConfirmed}
          />
        </Flex>
        <Flex
          vertical
          gap={5}
          style={{
            marginTop: '5px',
            marginBottom: '16px'
          }}
        >
          <Space size={[4, 0]}>
            <Text type='danger'>*</Text>
            <Text strong>Xác nhận nhân lực tăng ca</Text>
          </Space>
          <AddConfirmOvertimeTicket
            confirms={formState.confirms}
            handleSetFormState={handleSetFormState}
            handleDepartment={handleDepartment}
          />
        </Flex>
        <Form
          id='confirm-overtime-ticket'
          layout='vertical'
          form={formConfirm}
          fields={[
            {
              name: 'notecompensatoryLeave',
              value: autoNoteLeave(noteCompensatoryLeave)
            },
            {
              name: 'supervisorNote',
              value: formState.supervisorNote
            }
          ]}
        >
          <Form.Item
            label={<Text strong>Ghi chú xác nhận</Text>}
            name='supervisorNote'
            rules={[{ required: true, message: 'Không được để trống!' }]}
          >
            <TextArea
              onChange={(e) => setFormState((prev) => ({ ...prev, supervisorNote: e.target.value }))}
              rows={4}
              placeholder='Nhập ghi chú xác nhận'
            />
          </Form.Item>
          {openCompensatoryLeave && (
            <Flex vertical>
              <Divider dashed style={{ marginTop: 16, marginBottom: 16 }}>
                <Title level={5}>Thông tin phiếu nghỉ bù</Title>
              </Divider>
              <Form.Item
                label={<Text strong>Lý do nghỉ bù</Text>}
                name='notecompensatoryLeave'
                rules={[
                  {
                    required: true,
                    message: 'Không được để trống!'
                  }
                ]}
              >
                <TextArea rows={4} onChange={(e) => setNoteCompensatoryLeave(e.target.value)} />
              </Form.Item>
            </Flex>
          )}
        </Form>
        {openCompensatoryLeave && (
          <Flex vertical gap={10}>
            <Space size={[4, 0]}>
              <Tooltip title='Bắt buộc'>
                <Text type='danger'>*</Text>
              </Tooltip>
              <Text strong>Danh sách chi tiết phiếu nghỉ bù</Text>
            </Space>
            <AddLeaveTicketDetail
              handleChangeFormState={handleSetCompensatoryLeaveDetails}
              compensatoryLeaveDetails={compensatoryLeaveDetails}
              lastDateOvertime={confirmsList?.[confirmsList.length - 1]?.date as Date}
              overtimeEmployees={
                formState?.confirms
                  ?.filter((x) => !x.isSupervisorUnconfirmed)
                  ?.map((item) => item.employee) as IEmployeeShort[]
              }
            />
          </Flex>
        )}

        <Flex
          justify='center'
          align='center'
          className='question-compensatory-leave'
          style={{
            margin: '16px 0px'
          }}
        >
          <Checkbox
            checked={openCompensatoryLeave}
            onChange={(e) => {
              setOpenCompensatoryLeave(e.target.checked);
              if (!e.target.checked) {
                setCompensatoryLeaveDetails([]);
              }
              setTimeout(() => {
                const elementParent = document.querySelector('.question-compensatory-leave') as HTMLDivElement;
                elementParent &&
                  elementParent.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                  });
              }, 50);
            }}
          >
            <Text italic>Bạn muốn tạo phiếu nghỉ bù cho nhân viên ?</Text>
          </Checkbox>
        </Flex>
      </Flex>
    </Modal>
  );
}
